import { observable, computed, action, runInAction, reaction } from 'mobx';
import { message } from 'antd';
import { RouterStore, ProjectsRootVisualStore, UsersStore } from '../../common/stores';
import { MLService } from '../../ml_models/services';
import { MLModel } from '../../ml_models/types';
import { Package } from '../../common/models';
import { CreateEditTrainingProjectFormValues, TrainingProjectStatusDto, TrainingProjectImportResponse } from '../types';
import { ProjectsService } from '../../common/services';
import { SearchPackagesRequest } from '../../common/types';
import { PackageState } from '../../common/models';
import { PAGE_SIZE } from '../../common/stores/Pager';
import { TrainingProjectsNavigation } from '../routes';
import { TrainingProjectModel } from '../models';
import { TrainingProjectsService } from '../services';
import { showRuntimeSchemaValidationError } from '../utils/runtimeSchemaValidation';

type CreateEditFormValues = CreateEditTrainingProjectFormValues;

export default class TrainingProjectsStore {
    @observable
    trainingProjects: TrainingProjectModel[] = [];

    @observable
    trainingProjectsStatuses: TrainingProjectStatusDto[] = [];

    @observable
    mlModels: MLModel[] = [];

    @observable
    packages: Package[] = [];

    @observable
    createEditDialogVisible: boolean = false;

    @observable
    importDialogVisible: boolean = false;

    @observable
    importData: TrainingProjectImportResponse | null = null;

    @observable
    selectedReferenceModelId: string | null = null;

    @observable
    selectedTeacherModelId: string | null = null;

    @observable
    selectedJudgeModelId: string | null = null;

    @observable
    isLoadingTrainingProjects: boolean = false;

    @observable
    isLoadingMlModels: boolean = false;

    @observable
    isLoadingPackages: boolean = false;

    @observable
    isLoadingTrainingProjectsStatuses: boolean = false;

    @observable
    selectedTrainingProjectId: string | null = null;

    @computed
    get isLoadingUsers() {
        return this.usersStore.isLoading;
    }

    @computed
    get isLoading() {
        return (
            this.isLoadingTrainingProjects ||
            this.isLoadingTrainingProjectsStatuses ||
            this.isLoadingMlModels ||
            this.isLoadingUsers
        );
    }

    @computed
    get project() {
        return this.projectsStore.currentProject;
    }

    @computed
    get editableTrainingProject() {
        return this.trainingProjects.find(p => p.isEditable);
    }

    @computed
    get selectedTrainingProject() {
        return this.trainingProjects.find(p => p.id === this.selectedTrainingProjectId);
    }

    @computed
    get mlModelOptions() {
        return this.mlModels
            .filter(model => !!model.id && model.type === 'llm')
            .map(model => ({
                label: model.name,
                value: model.id!
            }));
    }

    @computed
    get packageOptions() {
        return this.packages.map(p => ({
            label: p.name,
            value: p.id
        }));
    }

    constructor(
        readonly routerStore: RouterStore,
        readonly projectsStore: ProjectsRootVisualStore,
        readonly usersStore: UsersStore,
        readonly service: TrainingProjectsService,
        readonly mlService: MLService,
        readonly projectsService: ProjectsService
    ) {
        reaction(
            () => this.project,
            () => {
                if (this.project) {
                    this.dispose();
                    this.initialize();
                }
            }
        );
    }

    @action.bound
    setCreateEditDialogVisible(createEditDialogVisible: boolean) {
        this.createEditDialogVisible = createEditDialogVisible;
    }

    @action.bound
    removeTrainingProject(trainingProject: TrainingProjectModel) {
        this.trainingProjects.splice(this.trainingProjects.indexOf(trainingProject), 1);
    }

    @action.bound
    setSelectedTrainingProjectId(selectedTrainingProjectId: string | null) {
        this.selectedTrainingProjectId = selectedTrainingProjectId;
    }

    @action.bound
    matchCurrentTrainingProjectData(params: Record<string, string | undefined>) {
        if (params.trainingProjectId) {
            this.setSelectedTrainingProjectId(params.trainingProjectId);
        }
    }

    @action.bound
    async createTrainingProject(formValues: CreateEditFormValues) {
        if (!this.project) {
            return;
        }

        const resp = await this.service.createTrainingProject(this.project.id, formValues);

        if (!resp.isOk()) {
            if (!showRuntimeSchemaValidationError(resp.error.data)) {
                message.error('Failed to create training project');
            }
            return false;
        }

        resp.map(data => {
            runInAction(() => {
                this.trainingProjects.push(new TrainingProjectModel(this, this.service, data));
            });
        });

        return true;
    }

    @action.bound
    async importTrainingProject(file: File) {
        if (!this.project) {
            return false;
        }

        try {
            const resp = await this.service.importTrainingProject(this.project.id, file);

            if (!resp.isOk()) {
                if (!showRuntimeSchemaValidationError(resp.error.data)) {
                    message.error('Failed to parse training project import file');
                }
                return false;
            }

            resp.map(data => {
                runInAction(() => {
                    this.importData = data;
                    this.importDialogVisible = true;
                });
            });

            return true;
        } catch (error) {
            console.error('Import error:', error);
            message.error('Failed to parse training project import file');
            return false;
        }
    }

    @action.bound
    setImportDialogVisible(visible: boolean) {
        this.importDialogVisible = visible;
    }

    @action.bound
    setSelectedReferenceModelId(modelId: string | null) {
        this.selectedReferenceModelId = modelId;
    }

    @action.bound
    setSelectedTeacherModelId(modelId: string | null) {
        this.selectedTeacherModelId = modelId;
    }

    @action.bound
    setSelectedJudgeModelId(modelId: string | null) {
        this.selectedJudgeModelId = modelId;
    }

    @action.bound
    resetImportData() {
        this.importData = null;
        this.selectedReferenceModelId = null;
        this.selectedTeacherModelId = null;
        this.selectedJudgeModelId = null;
    }

    @action.bound
    async commitImportedProject() {
        if (!this.project || !this.importData || !this.selectedReferenceModelId) {
            return false;
        }

        try {
            const resp = await this.service.commitImportTrainingProject(this.project.id, {
                importedData: this.importData.importedData,
                referenceModelId: this.selectedReferenceModelId,
                teacherModelId: this.selectedTeacherModelId || undefined,
                judgeModelId: this.selectedJudgeModelId || undefined
            });

            if (!resp.isOk()) {
                if (!showRuntimeSchemaValidationError(resp.error.data)) {
                    message.error('Failed to import training project');
                }
                return false;
            }

            resp.map(data => {
                runInAction(() => {
                    this.trainingProjects.push(new TrainingProjectModel(this, this.service, data));
                    this.importDialogVisible = false;
                    this.resetImportData();
                });
            });

            message.success('Training project imported successfully');
            return true;
        } catch (error) {
            console.error('Commit import error:', error);
            message.error('Failed to import training project');
            return false;
        }
    }

    @action.bound
    async loadTrainingProjects() {
        try {
            if (!this.project) {
                return;
            }

            runInAction(() => {
                this.isLoadingTrainingProjects = true;
            });

            const trainingProjects = await this.service.getTrainingProjects(this.project.id);

            runInAction(() => {
                this.trainingProjects = trainingProjects.map(
                    data => new TrainingProjectModel(this, this.service, data)
                );
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load training projects');
        } finally {
            runInAction(() => {
                this.isLoadingTrainingProjects = false;
            });
        }
    }

    @action.bound
    async loadTrainingProjectsStatuses(showLoading: boolean) {
        try {
            if (!this.project) {
                return;
            }

            if (showLoading) {
                runInAction(() => {
                    this.isLoadingTrainingProjectsStatuses = true;
                });
            }

            const trainingProjectsStatuses = await this.service.getTrainingProjectsStatuses(this.project.id);

            runInAction(() => {
                this.trainingProjectsStatuses = trainingProjectsStatuses;
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load training projects statuses');
        } finally {
            if (showLoading) {
                runInAction(() => {
                    this.isLoadingTrainingProjectsStatuses = false;
                });
            }
        }
    }

    @action.bound
    async loadMlModels() {
        if (!this.project) {
            return;
        }

        try {
            runInAction(() => {
                this.isLoadingMlModels = true;
            });

            const mlModels = await this.mlService.getMLModelsForProject(this.project.id);

            runInAction(() => {
                this.mlModels = mlModels;
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load ml models');
        } finally {
            runInAction(() => {
                this.isLoadingMlModels = false;
            });
        }
    }

    @action.bound
    async loadAllProjectPackages() {
        if (!this.project) {
            return;
        }

        try {
            runInAction(() => {
                this.isLoadingPackages = true;
            });

            let request: SearchPackagesRequest = {
                page: 0,
                pageSize: PAGE_SIZE,
                projectId: this.project.id,
                allSources: true,
                uploadedBy: this.projectsStore.hasAccessToAllEntities ? undefined : this.projectsStore.currentUserId,
                state: [PackageState.Ready]
            };

            const firstResponse = await this.projectsService.searchPackages(this.project, request);

            runInAction(() => {
                this.packages = firstResponse.lines;
            });

            if (firstResponse.lines.length < firstResponse.total) {
                const pages = Math.ceil(firstResponse.total / PAGE_SIZE);
                for (let i = 1; i < pages; i++) {
                    request.page = i;
                    const response = await this.projectsService.searchPackages(this.project, request);
                    runInAction(() => {
                        this.packages = this.packages.concat(response.lines);
                    });
                }
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to load packages');
        } finally {
            runInAction(() => {
                this.isLoadingPackages = false;
            });
        }
    }

    // Otherwise antd table will not be updated
    refreshTrainingProjects() {
        runInAction(() => {
            this.trainingProjects = [...this.trainingProjects];
        });
    }

    getUserNameById(id: string) {
        return this.usersStore.getUserNameById(id);
    }

    getMlModelNameById(id: string) {
        return this.mlModels.find(model => model.id === id)?.name ?? id;
    }

    navigateToTrainingProjectDashboard(trainingProjectId: string) {
        if (!this.project) {
            return;
        }

        this.routerStore.push(
            TrainingProjectsNavigation.TrainingProjectDashboardScreen.replace(':projectId', this.project.id).replace(
                ':trainingProjectId',
                trainingProjectId
            )
        );
    }

    initialize() {
        this.loadTrainingProjects();
    }

    dispose() {
        runInAction(() => {
            this.isLoadingTrainingProjects = false;
            this.isLoadingMlModels = false;
            this.createEditDialogVisible = false;
            this.trainingProjects = [];
            this.trainingProjectsStatuses = [];
            this.mlModels = [];
        });
    }
}
